import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  Video,
  Upload,
  Award,
  Calendar,
  DollarSign,
  Mail,
  CheckCircle,
  AlertCircle,
  Target,
  Users,
  ArrowRight,
  Play,
  Monitor,
  Volume2,
  Clock,
  Globe,
  CircleAlert,
  Loader2
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import SNIICongressInfoSection from '@/components/congresos/SNIICongressInfoSection.jsx';

// Reemplaza {{variable}} en un texto usando contexto de variables dadas
const interpolateText = (text, variables = {}) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
};

const CongresoPage = () => {
  const { id } = useParams();
  const [congreso, setCongreso] = useState(null);
  const [datosGlobales, setDatosGlobales] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar datos desde JSON unificado y datos generales
  useEffect(() => {
    const loadCongresoData = async () => {
      try {
        setIsLoading(true);

        // Cargar datos del congreso y datos generales en paralelo
        const [congresosResponse, generalesResponse] = await Promise.all([
          fetch(`/data/congresos-lista.json?v=${Date.now()}`),
          fetch(`/data/generales.json?v=${Date.now()}`)
        ]);

        if (!congresosResponse.ok) {
          throw new Error('Error al cargar datos de congresos');
        }
        if (!generalesResponse.ok) {
          throw new Error('Error al cargar datos generales');
        }

        const congresosData = await congresosResponse.json();
        const generalesData = await generalesResponse.json();

        // Obtener el congreso específico por ID
        const congresoData = congresosData.congresos[id];

        if (!congresoData) {
          throw new Error('Congreso no encontrado');
        }

        // Construir contexto de interpolación para templates
        const ctx = {
          numero: congresoData.numero || '',
          titulo: congresoData.titulo || '',
          nombreCorto: congresoData.nombreCorto || '',
          fechasEvento: congresosData.datosGlobales?.calendario?.fechasEvento || '',
          plazoEnvio: congresosData.datosGlobales?.calendario?.plazoEnvio || '',
          participantes: congresosData.datosGlobales?.participantes || '',
        };

        const textos = congresosData.datosGlobales?.textos || {};

        // Resolver descripcion/invitacion: si son tokens de template, expandirlos
        const resolveField = (value, templateKey) => {
          if (!value) return '';
          // Si el campo es literalmente "{{templateKey}}", usar el template de textos
          if (value.trim() === `{{${templateKey}}}`) {
            return interpolateText(textos[templateKey] || '', ctx);
          }
          return interpolateText(value, ctx);
        };

        const congresoResuelto = {
          ...congresoData,
          descripcion: resolveField(congresoData.descripcion, 'descripcionTemplate'),
          invitacion: resolveField(congresoData.invitacion, 'invitacionTemplate'),
        };

        setCongreso(congresoResuelto);
        setDatosGlobales(congresosData.datosGlobales);
        setDatosBancarios(generalesData.datosBancarios);
        
        // Construir planes dinámicamente desde todas las claves de costos
        if (congresosData.datosGlobales && congresosData.datosGlobales.costos) {
          const costos = congresosData.datosGlobales.costos;
          
          // Configuración visual por tipo de plan (se aplica según la clave)
          const estilosPlanes = {
            platino: {
              icono: Award,
              color: 'from-gray-500 to-gray-600',
              bgGradient: 'from-gray-100 to-gray-200',
              borderColor: 'border-gray-300',
              hoverBorder: 'hover:border-[#0b742f]',
              checkColor: 'text-gray-700',
              badgeBg: 'bg-gray-600',
              destacado: false
            },
            oro: {
              icono: Award,
              color: 'from-yellow-500 to-yellow-600',
              bgGradient: 'from-yellow-100 to-orange-100',
              borderColor: 'border-yellow-400',
              hoverBorder: 'hover:border-[#e87729]',
              checkColor: 'text-[#e87729]',
              badgeBg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
              destacado: true
            },
            plata: {
              icono: Award,
              color: 'from-slate-400 to-slate-500',
              bgGradient: 'from-slate-50 to-slate-100',
              borderColor: 'border-slate-300',
              hoverBorder: 'hover:border-slate-500',
              checkColor: 'text-slate-600',
              badgeBg: 'bg-slate-500',
              destacado: false
            },
            bronce: {
              icono: Award,
              color: 'from-amber-600 to-amber-700',
              bgGradient: 'from-amber-50 to-amber-100',
              borderColor: 'border-amber-400',
              hoverBorder: 'hover:border-amber-600',
              checkColor: 'text-amber-700',
              badgeBg: 'bg-amber-600',
              destacado: false
            },
            // Estilo por defecto para nuevos planes
            default: {
              icono: Award,
              color: 'from-blue-500 to-blue-600',
              bgGradient: 'from-blue-50 to-blue-100',
              borderColor: 'border-blue-300',
              hoverBorder: 'hover:border-blue-600',
              checkColor: 'text-blue-700',
              badgeBg: 'bg-blue-600',
              destacado: false
            }
          };
          
          // Generar planes dinámicamente desde todas las claves de costos
          const planesArr = Object.keys(costos).map(clavePlan => {
            const datosPlan = costos[clavePlan];
            const estilos = estilosPlanes[clavePlan.toLowerCase()] || estilosPlanes.default;
            
            // Combinar beneficios + revistas en una sola lista presentable
            const beneficiosList = Array.isArray(datosPlan.beneficios) ? datosPlan.beneficios : [];
            const revistasList = Array.isArray(datosPlan.revistas) ? datosPlan.revistas : [];

            return {
              nombre: clavePlan.charAt(0).toUpperCase() + clavePlan.slice(1),
              ...estilos,
              descripcion: datosPlan.descripcion,
              beneficios: beneficiosList,
              revistas: revistasList,
              precio: datosPlan.precio,
              precioAlternativo: datosPlan.precioAlternativo || null,
              moneda: datosPlan.moneda,
              tipo: datosPlan.tipo,
              tipoAlternativo: datosPlan.tipoAlternativo || null,
              nota: datosPlan.nota,
              destacado: datosPlan.destacado !== undefined ? datosPlan.destacado : estilos.destacado
            };
          });
          
          setPlanes(planesArr);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error cargando datos del congreso:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadCongresoData();
    }
  }, [id]);

  // Mostrar estado de carga
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información del congreso...</p>
        </div>
      </div>
    );
  }

  // Mostrar error
  if (error || !congreso || !datosGlobales) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center bg-white rounded-2xl shadow-lg p-12 border-l-4 border-red-500">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Congreso no encontrado</h1>
            <p className="text-gray-600">El congreso solicitado no existe o no está disponible.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section con imagen de fondo */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        {/* Imagen de fondo con overlay */}
        <div className="absolute inset-0">
          <img
            src="https://recursos.cenid.org/img_cenid/congreso_generalx.png"
            alt="Fondo congreso"
            className="w-full h-full object-cover opacity-10"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <Users className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                {congreso.numero} {congreso.titulo}, {congreso.nombreCorto}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm md:text-base">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>{datosGlobales.calendario.fechasEvento}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span>{datosGlobales.participantes}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  <span>{datosGlobales.modalidad}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Tarjetas de información */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Fechas */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-[#0b742f] hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <Calendar className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Fechas</h3>
            </div>
            <p className="text-gray-600">{datosGlobales.calendario.fechasEvento}</p>
          </div>

          {/* Participantes */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-[#e87729] hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Users className="w-6 h-6 text-[#e87729]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Participantes</h3>
            </div>
            <p className="text-gray-600">{datosGlobales.participantes}</p>
          </div>

          {/* Modalidad */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-500 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <Target className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Modalidad</h3>
            </div>
            <p className="text-gray-600">{datosGlobales.modalidad}</p>
          </div>
        </div>

        {/* Descripción y Propósito */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#0b742f]">
            <div className="space-y-6">
              <div 
                className="text-gray-700 leading-relaxed text-justify"
                dangerouslySetInnerHTML={{ __html: congreso.descripcion }}
              />

              <div 
                className="text-gray-700 leading-relaxed text-justify"
                dangerouslySetInnerHTML={{ __html: congreso.proposito }}
              />

              <div className="bg-green-50 border-l-4 border-[#0b742f] p-4 rounded-r">
                <div 
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: congreso.invitacion }}
                />
              </div>
            </div>
          </div>
        </section>

        <SNIICongressInfoSection />

        <Accordion type="multiple" className="space-y-8 mb-12">
          {/* Áreas temáticas */}
          <AccordionItem value="areas-tematicas" className="border-none">
            <section>
              <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
                <AccordionTrigger className="px-8 py-6 text-left hover:no-underline [&>svg]:text-[#0b742f]">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Target className="w-8 h-8 text-[#0b742f]" />
                      <h2 className="text-3xl font-bold text-gray-900">Áreas Temáticas</h2>
                    </div>
                    <div className="h-1 w-32 bg-[#e87729] rounded ml-11"></div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-8 pb-8 pt-0 text-base">
                  <p className="text-gray-700 mb-6">
                    {(datosGlobales.textos?.areasIntro) || 'Los temas del congreso incluyen, pero no se limitan a las siguientes áreas del conocimiento del Consejo Nacional de Ciencia y Tecnología (SECIHTI):'}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                    {congreso.areasTemáticas.map((area, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-[#0b742f]/5 transition-colors">
                        <svg className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                        <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: area }} />
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </div>
            </section>
          </AccordionItem>

          {/* Normas de presentación para capítulo de libro y artículo en revista */}
          <AccordionItem value="normas-presentacion" className="border-none">
            <section>
              <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
                <AccordionTrigger className="px-8 py-6 text-left hover:no-underline [&>svg]:text-[#0b742f]">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-8 h-8 text-[#0b742f]" />
                      <h2 className="text-3xl font-bold text-gray-900">Normas de Presentación</h2>
                    </div>
                    <div className="h-1 w-32 bg-[#e87729] rounded ml-11"></div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-8 pb-8 pt-0 text-base">
                  <p className="text-gray-700 mb-6">{datosGlobales.textos?.normasPresentacionSubtitulo || 'para Capítulo de Libro y Artículo en Revista'}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-[#0b742f]">
                      <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        Se admitirá un máximo de <strong>{datosGlobales.normas.maxPublicaciones} publicaciones</strong> individuales o colectivas por persona inscrita
                      </p>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        Formato requerido: <strong>{datosGlobales.normas.formato}</strong>
                      </p>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-[#0b742f]">
                      <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        Se admitirá un máximo de <strong>{datosGlobales.normas.maxAutores} autores</strong> por publicación colectiva
                      </p>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        Tamaño máximo: <strong>{datosGlobales.normas.tamañoMax}</strong>
                      </p>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-[#0b742f]">
                      <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        Todos los autores de cada artículo deberán estar inscritos en el congreso
                      </p>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                      <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">
                        No se admitirán trabajos que no cumplan con las normas especificadas
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </div>
            </section>
          </AccordionItem>

          {/* Normas de presentación del vídeo */}
          <AccordionItem value="video-defensa" className="border-none">
            <section>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-[#0b742f]">
                <AccordionTrigger className="px-8 py-6 md:px-12 md:py-8 text-left hover:no-underline [&>svg]:text-[#0b742f]">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Video className="w-8 h-8 text-[#0b742f]" />
                      <h2 className="text-3xl font-bold text-gray-900">Pautas y recomendaciones para grabar la video-defensa de una ponencia</h2>
                    </div>
                    <div className="h-1 w-24 bg-[#e87729] rounded"></div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-8 pb-8 pt-0 text-base md:px-12 md:pb-12">
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Play className="w-6 h-6 text-[#0b742f]" />
                      Grabación del vídeo
                    </h3>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Técnicas de grabación disponibles:</h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-3">
                          <ArrowRight className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">Presentación personal ante la cámara sin soporte visual</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <ArrowRight className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">Presentación mixta combinando exposición ante la cámara con diapositivas en pantalla</span>
                        </li>
                      </ul>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 rounded-lg border-l-4 border-green-500 bg-green-50">
                          <Users className="w-6 h-6 mt-1 flex-shrink-0 text-green-600" />
                          <span className="text-gray-700 leading-relaxed">No es imprescindible, aunque sí es recomendable que todas las personas firmantes de una ponencia participen en la video defensa de la misma.</span>
                        </div>
                        <div className="flex items-start gap-4 p-4 rounded-lg border-l-4 border-red-500 bg-red-50">
                          <CircleAlert className="w-6 h-6 mt-1 flex-shrink-0 text-red-600" />
                          <span className="text-gray-700 leading-relaxed">No se admitirán vídeos que solo contengan texto escrito: la "presencia humana" ya sea en vídeo o audio es necesaria.</span>
                        </div>
                        <div className="flex items-start gap-4 p-4 rounded-lg border-l-4 border-green-500 bg-green-50">
                          <Monitor className="w-6 h-6 mt-1 flex-shrink-0 text-green-600" />
                          <span className="text-gray-700 leading-relaxed">Se ruega grabar el vídeo en formato horizontal.</span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                          <Volume2 className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                          <span className="text-gray-700 leading-relaxed">Es fundamental que el sonido sea adecuado, por lo que es necesario elegir un espacio con baja reverberación y poco o nulo sonido ambiente.</span>
                        </div>
                        <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                          <Clock className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                          <span className="text-gray-700 leading-relaxed">La duración del vídeo no debe ser inferior a los 5 minutos ni exceder los 10.</span>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-400">
                          <h5 className="font-semibold text-gray-900 mb-2">Medios técnicos sugeridos:</h5>
                          <p className="text-gray-700">Meet, Zoom, Teams, etc.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Upload className="w-6 h-6 text-[#e87729]" />
                      Subir el vídeo a una cuenta de YouTube
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 text-lg">Proceso de subida</h4>
                        <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                          <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">1</div>
                          <span className="text-gray-700 leading-relaxed">Una vez grabado el vídeo con los medios técnicos que considere (Meet, Zoom, Teams, etc.), se deberá subir a una cuenta propia de YouTube, activando la opción visibilidad pública.</span>
                        </div>
                        <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                          <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">2</div>
                          <span className="text-gray-700 leading-relaxed">Si no activa la visibilidad pública, el vídeo no se verá embebido en la plataforma, aunque se podrá acceder a él mediante el enlace que se muestre.</span>
                        </div>
                        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                          <h5 className="font-semibold text-green-800 mb-2">💡 Ayuda para crear cuenta:</h5>
                          <p className="text-green-700 text-sm mb-2">Si no tiene una cuenta en YouTube podrá crearla de forma muy sencilla.</p>
                          <p className="text-green-700 text-sm">Las cuentas de Gmail tienen asociada una cuenta de YouTube por defecto.</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 text-lg">Permanencia y alternativas</h4>
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                          <h5 className="font-semibold text-purple-800 mb-2">⏰ Tiempo recomendado online:</h5>
                          <p className="text-purple-700 text-sm mb-2"><strong>Recomendado:</strong> un año después de la finalización del congreso</p>
                          <p className="text-purple-700 text-sm mb-2"><strong>Mínimo:</strong> al menos tres meses</p>
                          <p className="text-purple-700 text-sm">Con el tiempo los buscadores lo indexarán y pasará a ser un contenido de calidad accesible y disponible para cualquier persona.</p>
                        </div>
                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                          <h5 className="font-semibold text-amber-800 mb-2">⚠️ Alternativas por copyright:</h5>
                          <p className="text-amber-700 text-sm mb-2">Si, por razones de copyright, su vídeo es censurado en YouTube, puede subirlo a otro tipo de plataforma.</p>
                          <p className="text-amber-700 text-sm"><strong>Limitación:</strong> en estos casos el vídeo no se verá embebido en la página.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#0b742f]/10 border border-[#0b742f]/20 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <Mail className="w-8 h-8 text-[#0b742f] flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-[#0b742f] text-lg mb-2">Envío Final</h4>
                        <p className="text-gray-700 leading-relaxed mb-3">El envío de presentaciones en video y trabajos finales solo será válido por esta vía por la vía del correo electrónico institucional del congreso:</p>
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 text-[#0b742f]" />
                          <a href={`mailto:${congreso.generales.correo}`} className="text-[#0b742f] hover:text-[#0a5f26] font-semibold underline text-lg">{congreso.generales.correo}</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </div>
            </section>
          </AccordionItem>

          {/* Envío de archivos finales */}
          <AccordionItem value="envio-archivos" className="border-none">
            <section>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-[#0b742f]">
                <AccordionTrigger className="px-8 py-6 md:px-12 md:py-8 text-left hover:no-underline [&>svg]:text-[#0b742f]">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Upload className="w-8 h-8 text-[#0b742f]" />
                      <h2 className="text-3xl font-bold text-gray-900">Envío de archivos finales</h2>
                    </div>
                    <div className="h-1 w-24 bg-[#e87729] rounded"></div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-8 pb-8 pt-0 text-base md:px-12 md:pb-12">
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl mb-8">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-red-800 mb-2">Nota importante antes de enviar sus archivos al congreso en extenso:</p>
                        <p className="text-red-700">Es fundamental que antes y durante la elaboración de sus artículos se sigan al pie de la letra los requisitos que deben cumplir ambos trabajos.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
                    <h3 className="font-semibold text-blue-900 mb-3 text-lg">📋 Instrucciones Generales</h3>
                    <div className="space-y-3 text-blue-800">
                      <p>La gestión del envío de archivos ha cambiado, por lo que le sugerimos seguir cuidadosamente las instrucciones.</p>
                      <p>Una vez elaborado el o los artículos y la presentación en video se deberán enviar a través del correo institucional del congreso: <a href={`mailto:${congreso.generales.correo}`} className="text-[#0b742f] underline font-semibold hover:text-[#0a5f26]">{congreso.generales.correo}</a>. Ambos deberán cumplir con los lineamientos de publicación.</p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-6 h-6 text-[#e87729]" />
                      Requisitos Técnicos
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-700"><strong>No se aceptan:</strong></p>
                          <p className="text-gray-700 text-sm">Archivos en PDF o carpetas comprimidas con diferentes archivos</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-[#0b742f]">
                        <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-700"><strong>Formato requerido:</strong></p>
                          <p className="text-gray-700 text-sm">{datosGlobales.normas.formato} (plantilla oficial)</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-[#0b742f]">
                        <CheckCircle className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-700"><strong>Contenido:</strong></p>
                          <p className="text-gray-700 text-sm">Debe incluir todos los textos, gráficos e imágenes</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                        <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-700"><strong>Tamaño máximo:</strong></p>
                          <p className="text-gray-700 text-sm">10 Mb (cuidado con imágenes en alta resolución)</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-6 h-6 text-[#0b742f]" />
                      Proceso de Revisión por Pares
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-[#0b742f] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">✓</div>
                        <p className="text-gray-700">En todas nuestras publicaciones electrónicas se trabaja el <strong>arbitraje por pares</strong> donde se concibe la revisión desde una perspectiva constructiva e integradora, proporcionando un marco para la revisión por pares basada en una <strong>evaluación doblemente anónima</strong>, permitiendo a los autores publicar en una revista académica con los más altos estándares de calidad.</p>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-[#0b742f] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">✓</div>
                        <p className="text-gray-700">La Comisión acepta papeles que <strong>no aparecen en ninguna otra fuente</strong>, y que no son objeto de examen o sometido a cualquier otra conferencia o evento de naturaleza similar.</p>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-[#0b742f] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">✓</div>
                        <p className="text-gray-700">El Equipo Editorial se reserva el derecho de devolver a los autores los textos que no cumplan con las normas editoriales aquí especificadas.</p>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-[#0b742f] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">✓</div>
                        <p className="text-gray-700">Todos los trabajos, incluyendo aquellos del <strong>Equipo Editorial</strong>, serán sometidos al mismo proceso de revisión.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="w-6 h-6 text-[#e87729]" />
                      Plazos y Modificaciones
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                        <h4 className="font-semibold text-amber-900 mb-2">⏰ Aceptación del trabajo</h4>
                        <p className="text-amber-800 text-sm mb-2">Una vez aceptado el trabajo para su publicación, los revisores y/o el Equipo Editorial harán observaciones que el (los) autor(es) debe(n) atender.</p>
                        <p className="text-amber-800 text-sm"><strong>Plazo máximo:</strong> 2 semanas</p>
                      </div>

                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                        <h4 className="font-semibold text-amber-900 mb-2">📝 Modificaciones</h4>
                        <p className="text-amber-800 text-sm mb-2">Las modificaciones deben sujetarse sólo a lo indicado por los revisores y/o el Equipo Editorial.</p>
                        <p className="text-amber-800 text-sm"><strong>Importante:</strong> Seguir únicamente las indicaciones proporcionadas.</p>
                      </div>

                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5" />
                          Baja automática
                        </h4>
                        <p className="text-red-800 text-sm">Cuando el autor demore <strong>más de 2 semanas</strong> en responder a las modificaciones indicadas, el artículo será dado de baja.</p>
                      </div>

                      <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                        <h4 className="font-semibold text-purple-900 mb-2">📅 Fechas límite</h4>
                        <p className="text-purple-800 text-sm">Consulte en el apartado <strong>fechas y plazos</strong> de esta web, el plazo de aceptación de textos.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-100 border-l-4 border-gray-500 p-6 rounded-r-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-gray-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">🔒 Cierre del Sistema</h4>
                        <p className="text-gray-700">Una vez pasados los plazos el sistema realizará una descarga de los archivos subidos y se desactivará la posibilidad de añadir nuevos documentos.</p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </div>
            </section>
          </AccordionItem>

          {/* Constancias de participación */}
          <AccordionItem value="constancias" className="border-none">
            <section>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-l-4 border-[#0b742f]">
                <AccordionTrigger className="px-8 py-6 md:px-12 md:py-8 text-left hover:no-underline [&>svg]:text-[#0b742f]">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Award className="w-8 h-8 text-[#0b742f]" />
                      <h2 className="text-3xl font-bold text-gray-900">Constancias de Participación</h2>
                    </div>
                    <div className="h-1 w-24 bg-[#e87729] rounded"></div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-8 pb-8 pt-0 text-base md:px-12 md:pb-12">
                  <div className="bg-gradient-to-r from-[#0b742f]/5 to-blue-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg mb-8">
                    <div className="flex items-start gap-3">
                      <Award className="w-8 h-8 text-[#0b742f] flex-shrink-0" />
                      <div>
                        <h3 className="font-bold text-[#0b742f] text-lg mb-2">¿Cómo obtener tu constancia?</h3>
                        <p className="text-gray-700">Las constancias se entregarán a todos los autores registrados que cumplan con los requisitos establecidos.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-6 h-6 text-[#0b742f]" />
                      Requisitos para Obtener la Constancia
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold flex-shrink-0">1</div>
                          <h4 className="font-semibold text-green-900 text-lg">Participación Activa</h4>
                        </div>
                        <p className="text-green-800 ml-11">Se entregará a cada autor/a registrado una constancia por ponencia, siempre y cuando el sistema registre que cada participante comente como mínimo en el <strong>{datosGlobales.normas.participacionMinima} de las presentaciones</strong>.</p>
                      </div>

                      <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold flex-shrink-0">2</div>
                          <h4 className="font-semibold text-amber-900 text-lg">Registro Correcto</h4>
                        </div>
                        <p className="text-amber-800 ml-11">El registro de los nombres en las constancias será con apego a los nombres escritos en los trabajos. <strong>Tenga cuidado en su correcta escritura</strong>.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-6 h-6 text-[#e87729]" />
                      Proceso de Elaboración
                    </h3>

                    <div className="bg-blue-50 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold flex-shrink-0 mt-1">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-blue-900 mb-2 text-lg">Generación de Constancias</h4>
                          <p className="text-blue-800 mb-3">Las constancias de participación serán elaboradas por el <strong>responsable o titular de cada ponencia</strong> vía formulario electrónico.</p>
                          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="w-5 h-5 text-blue-600" />
                              <p className="font-semibold text-blue-900">Disponibilidad:</p>
                            </div>
                            <p className="text-blue-800 text-sm">A partir del <strong>día siguiente a la finalización del evento</strong></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-purple-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-purple-900 mb-2">📌 Información Importante</h4>
                        <ul className="space-y-2 text-purple-800">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-1" />
                            <span className="text-sm">Las constancias finales se podrán generar el mismo día que finalice el evento</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-1" />
                            <span className="text-sm">Cada autor recibirá una constancia por cada ponencia en la que participe</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 mt-1" />
                            <span className="text-sm">Verifique que sus datos estén correctamente capturados antes del cierre del evento</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </div>
            </section>
          </AccordionItem>
        </Accordion>

        {/* Calendario */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-6">
                <div className="bg-[#0b742f]/10 p-2 rounded">
                  <Calendar className="w-6 h-6 text-[#0b742f]" />
                </div>
                Calendario
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Upload className="w-6 h-6 text-[#e87729]" />
                    <h3 className="font-bold">Envío de contribuciones</h3>
                  </div>
                  <p className="text-lg font-semibold">{datosGlobales.calendario.plazoEnvio}</p>
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-6 h-6" />
                    <h3 className="font-bold">Notificación de aceptación</h3>
                  </div>
                  <p className="text-lg font-semibold">{datosGlobales.calendario.notificacionAceptacion}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="w-6 h-6" />
                    <h3 className="font-bold">Inscripción autores</h3>
                  </div>
                  <p className="text-lg font-semibold">{datosGlobales.calendario.inscripcionAutores}</p>
                </div>

                <div className="bg-gradient-to-br from-[#e87729] to-[#c76520] text-white p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-6 h-6" />
                    <h3 className="font-bold">Discusión de contribuciones</h3>
                  </div>
                  <p className="text-lg font-semibold">{datosGlobales.calendario.fechasEvento}</p>
                </div>
              </div>

              <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-yellow-800 space-y-2">
                    <p><strong>Importante:</strong> Las contribuciones se publicarán siempre y cuando el autor o los autores se registren antes del: <strong>{datosGlobales.calendario.inscripcionAutores.split(' - ')[1] || datosGlobales.calendario.inscripcionAutores}</strong></p>
                    <p>Las constancias finales se podrán generar el mismo día que finalice el evento</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nota importante */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 rounded-r-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-start gap-4 mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
              <h3 className="text-2xl font-bold text-yellow-900">
                {datosGlobales.textos?.notaImportante?.titulo || 'Nota importante previo enviar su extenso'}
              </h3>
            </div>
            <div className="space-y-4 text-yellow-900">
              {(datosGlobales.textos?.notaImportante?.parrafos || [
                'Es de suma importancia que previo y durante la elaboración de su artículo siga al pie de la letra los requerimientos que deben de cumplir cada uno de los extensos a ser publicados. El comité editorial del CENID tiene la facultad de rechazar cualquier trabajo que no cumpla con los lineamientos expuestos.',
                'Es necesario recordar que la cuota de inscripción es individual por autor y no por trabajo presentado, es decir, cada uno de los autores que aparezcan en el trabajo deberá realizar su pago para que pueda tener acceso a la publicación de su trabajo en la opción de publicación elegida, así como al certificado de participación. En los casos en que un participante aparezca en más de un trabajo éste deberá de pagar cuota de inscripción por cada trabajo presentado.',
                'La discusión de contribuciones es la fase final del congreso, el espacio de discusión, reflexión y análisis de todos los contenidos presentados a lo largo del congreso, y que tendrá lugar on-line. La clave y contraseña de la plataforma a utilizar, será enviada por correo electrónico a los participantes.'
              ]).map((parrafo, i) => (
                <p key={i} className="leading-relaxed">{parrafo}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Costos */}
        <section className="mb-12">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-3 flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-[#0b742f]" />
              Costos e Inscripción
            </h2>
            <div className="h-1 w-24 bg-[#e87729] rounded"></div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="space-y-3 text-gray-700">
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                  <span>Cada autor puede participar en un máximo de <strong>{datosGlobales.normas.maxArticulosPorAutor} artículos</strong></span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                  <span>Cada artículo podrá tener un máximo de <strong>{datosGlobales.normas.maxAutores} autores</strong></span>
                </p>
                <p className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-0.5" />
                  <span>Todas las personas que consten como autores/as deben abonar la cuota correspondiente</span>
                </p>
              </div>
            </div>

            {planes.length > 0 && (
              <>
               {/* <h3 className="text-2xl font-bold text-gray-900">Opciones de participación</h3> */}

                <div className={`grid grid-cols-1 ${planes.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-2xl mx-auto'} gap-6`}>
                  {planes.map((plan, idx) => (
                    <div
                      key={idx}
                      className={`group bg-gradient-to-br ${plan.bgGradient} rounded-2xl p-8 border-2 ${plan.borderColor} ${plan.hoverBorder} transition-all duration-300 hover:shadow-2xl relative overflow-hidden`}
                    >
                      {plan.destacado && (
                        <div className="absolute top-4 right-4 bg-[#e87729] text-white px-3 py-1 rounded-full text-xs font-bold">
                          RECOMENDADO
                        </div>
                      )}
                      
                      <div className="text-center mb-6">
                        <div className={`inline-block ${plan.badgeBg} text-white px-4 py-2 rounded-full text-sm font-bold mb-3`}>
                          {plan.nombre.toUpperCase()}
                        </div>
                        {/* Precio principal */}
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-5xl font-bold text-gray-900">${plan.precio}</span>
                          <span className="text-gray-600">.00</span>
                        </div>
                        <p className="text-gray-600 mt-1 text-sm">{plan.moneda} — {plan.tipo}</p>
                        {/* Precio alternativo (ej. no miembros) */}
                        {plan.precioAlternativo && (
                          <div className="mt-3 bg-white/70 rounded-lg px-4 py-2 inline-block">
                            <div className="flex items-baseline justify-center gap-1">
                              <span className="text-2xl font-bold text-gray-700">${plan.precioAlternativo}</span>
                              <span className="text-gray-500 text-sm">.00</span>
                            </div>
                            <p className="text-gray-500 text-xs mt-0.5">{plan.moneda} — {plan.tipoAlternativo}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-4">
                        {plan.descripcion && <p className="text-gray-700 font-semibold">{plan.descripcion}</p>}
                        {/* Beneficios del plan */}
                        {plan.beneficios.length > 0 && (
                          <div className="space-y-2">
                            {plan.beneficios.map((beneficio, index) => (
                              <div key={index} className="flex items-start gap-2">
                                <CheckCircle className={`w-5 h-5 ${plan.checkColor} flex-shrink-0 mt-0.5`} />
                                <span className="text-sm text-gray-700">{beneficio}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Revistas donde se publica */}
                        {plan.revistas.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-black/10">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Los artículos científicos serán publicados en:</p>
                            <div className="space-y-1">
                              {plan.revistas.map((revista, index) => (
                                <div key={index} className="flex items-start gap-2">
                                  <ArrowRight className={`w-3.5 h-3.5 ${plan.checkColor} flex-shrink-0 mt-0.5`} />
                                  <span className="text-xs text-gray-600">{revista}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {plan.nota && (
                          <div className="bg-red-100 border-l-4 border-red-500 p-3 rounded-r mt-4">
                            <p className="text-xs text-red-800 font-semibold">NOTA: {plan.nota}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Información de pago */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="w-8 h-8 text-blue-600" />
                <h4 className="text-xl font-bold text-blue-900">Información de Pago</h4>
              </div>

              <div className="space-y-4 mb-6">
                {(datosGlobales.textos?.informacionPago?.instrucciones || [
                  'Por favor realiza tus depósitos sólo en la cuenta autorizada',
                  'Los pagos deberán efectuarse vía banca electrónica',
                  'Pagos desde el extranjero: PayPal o Western Union (+5% comisión)'
                ]).map((instruccion, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white/60 p-4 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-blue-900">{instruccion}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-300">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">Datos Bancarios</h5>
                {datosBancarios && (
                  <div className="space-y-3 text-sm text-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="font-semibold text-blue-900">Banco:</p>
                        <p>{datosBancarios.banco}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="font-semibold text-blue-900">Titular:</p>
                        <p>{datosBancarios.titular}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="font-semibold text-blue-900">Número de Cuenta:</p>
                        <p className="font-mono bg-gray-100 px-3 py-2 rounded">{datosBancarios.numeroCuenta}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">CLABE Interbancaria:</p>
                        <p className="font-mono bg-gray-100 px-3 py-2 rounded">{datosBancarios.clabe}</p>
                      </div>
                    </div>
                    <div>
                      <p className="font-semibold text-blue-900">Sucursal:</p>
                      <p>{datosBancarios.sucursal}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-blue-900">SWIFT:</p>
                      <p className="font-mono bg-gray-100 px-3 py-2 rounded inline-block">{datosBancarios.swift}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-3">
                <div className="bg-white/60 p-4 rounded-lg">
                  <p className="text-blue-900">
                    <strong>Reportar pago:</strong> Enviar comprobante a <a href={`mailto:${congreso.generales.correo}`} className="text-blue-600 underline font-semibold">{congreso.generales.correo}</a>
                  </p>
                </div>
                <div className="bg-white/60 p-4 rounded-lg">
                  <p className="text-blue-900">
                    <strong>Facturación:</strong> Indicarlo en el correo con el comprobante de pago, anexando datos fiscales
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contacto final */}
        <section>
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-4">
              <Mail className="w-8 h-8 text-[#e87729]" />
              <h2 className="text-2xl font-bold">Contacto</h2>
            </div>
            <p className="text-lg text-white/95">
              Para cualquier asunto con este congreso, por favor envía un correo a: <a href={`mailto:${congreso.generales.correo}`} className="text-[#e87729] font-bold underline">{congreso.generales.correo}</a> donde uno de nuestros asesores académicos lo atenderá.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default CongresoPage;
